import { useState, type FormEvent } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthLayout } from '@/components/AuthLayout'

const API_BASE = import.meta.env.VITE_API_URL || ''

export function ResetPassword() {
  const { t } = useTranslation(['auth', 'common'])
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError(t('errors.passwordTooShort'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('errors.passwordsDoNotMatch'))
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || t('errors.resetFailed'))
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.resetFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>{success ? t('resetPassword.successTitle') : t('resetPassword.title')}</CardTitle>
            <CardDescription>
              {success
                ? t('resetPassword.successSubtitle')
                : t('resetPassword.subtitle')}
            </CardDescription>
          </CardHeader>

          {success ? (
            <CardFooter>
              <Button className="w-full" onClick={() => navigate('/login')}>
                {t('resetPassword.goToSignIn')}
              </Button>
            </CardFooter>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">{t('fields.newPassword')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('fields.minCharsPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('fields.confirmPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder={t('fields.reenterPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-4">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
                </Button>
                <p className="text-sm text-muted-foreground">
                  <Link to="/login" className="text-primary hover:underline font-medium">
                    {t('resetPassword.backToSignIn')}
                  </Link>
                </p>
              </CardFooter>
            </form>
          )}
        </Card>
    </AuthLayout>
  )
}
